import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { CoreInput } from '../CoreInput';
import { classes as inputWithOptionsClasses } from '../CoreInputWithOptons/CoreInputWithOptions.st.css';
import { classes as popoverClasses } from '../CorePopover/CorePopover.st.css';
import { FloatingDropdown } from '../FloatingDropdown';
import { classes } from './floating-input-with-options.st.css';
const NAVIGATION_CONTROL_KEYS = [
    'ArrowDown',
    'ArrowUp',
    'Escape',
    'Enter',
    'Tab'
];
// Stable reference — prevents FloatingDropdown's selectedIds sync effect from re-running on every keystroke.
const EMPTY_SELECTED_IDS = [];
const defaultFilterPredicate = (inputValue, optionValue)=>optionValue.toLowerCase().includes(inputValue.toLowerCase());
/**
 * Replacement for CoreInputWithOptions using floating-ui based FloatingDropdown
 */ export const FloatingInputWithOptions = ({ id, className, inputProps, options, filterPredicate = defaultFilterPredicate, onSelect, onManualInput, contentRole = 'listbox' })=>{
    const dropdownRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [ariaActivedescendant, setAriaActivedescendant] = useState(null);
    const [ariaExpanded, setAriaExpanded] = useState(false);
    const contentId = id ? `${id}-content` : undefined;
    const filteredOptions = getFilteredOptions(options, inputProps.value, isEditing, filterPredicate);
    const firstOptionId = filteredOptions.length ? filteredOptions[0].id : undefined;
    const handleSelect = useCallback((option)=>{
        setIsEditing(false);
        setAriaActivedescendant(null);
        onSelect(option);
    }, [
        onSelect
    ]);
    const handleKeyDown = useCallback((event)=>{
        if (!ariaExpanded && [
            'ArrowUp',
            'ArrowDown',
            'Enter'
        ].includes(event.key)) {
            // When closed, reopen suggestions without selecting one.
            event.preventDefault();
            dropdownRef.current?.open();
            inputProps.onKeyDown?.(event);
            return;
        }
        if (event.key === ' ' && !ariaExpanded) {
            // Space opens the closed panel without inserting text.
            event.preventDefault();
        }
        if (!NAVIGATION_CONTROL_KEYS.includes(event.key)) {
            setIsEditing(true);
            setAriaActivedescendant(null);
            // Match CoreDropdown: only character keys reopen dismissed suggestions.
            if (/^.$/u.test(event.key)) {
                dropdownRef.current?.open();
            }
        }
        if ((event.key === 'Tab' || event.key === 'Enter') && ariaExpanded && !filteredOptions.length) {
            // With no suggestions (including when loading), commit manual input and close.
            event.preventDefault();
            setIsEditing(false);
            onManualInput(inputProps.value ?? '');
            dropdownRef.current?.close();
        } else {
            // Delegate option selection and keyboard navigation to the dropdown.
            dropdownRef.current?.onKeyDown(event);
        }
        inputProps.onKeyDown?.(event);
    }, [
        inputProps,
        ariaExpanded,
        filteredOptions.length,
        onManualInput
    ]);
    const handleFocus = useCallback((event)=>{
        setIsEditing(false);
        inputProps.onFocus?.(event);
    }, [
        inputProps
    ]);
    const handleBlur = useCallback((event)=>{
        if (isEditing) {
            // User typed but did not select an option — treat as manual input.
            onManualInput(inputProps.value ?? '');
        }
        setIsEditing(false);
        inputProps.onBlur?.(event);
    }, [
        inputProps,
        onManualInput,
        isEditing
    ]);
    const handleOptionHover = useCallback((option)=>{
        setAriaActivedescendant(option?._DOMid ?? null);
    }, []);
    const handleExpandedChange = useCallback((expanded)=>{
        setAriaExpanded(expanded);
    }, []);
    const handleContentMouseDown = useCallback((event)=>{
        setIsEditing(false);
        // Keep the input focused until the option click selects its value.
        event.preventDefault();
    }, []);
    return /*#__PURE__*/ React.createElement(FloatingDropdown, {
        ref: dropdownRef,
        className: classNames(popoverClasses.root, className),
        contentClassName: classes.floatingContent,
        dropdownContentClassName: classes.floatingDropdownContent,
        matchTriggerWidth: true,
        contentOffset: -1,
        placement: "bottom-start",
        openTrigger: "click",
        options: filteredOptions,
        initialSelectedIds: EMPTY_SELECTED_IDS,
        focusOnOption: firstOptionId,
        onSelect: handleSelect,
        onOptionHover: handleOptionHover,
        onExpandedChange: handleExpandedChange,
        contentRole: contentRole,
        contentId: contentId,
        disabled: inputProps.disabled,
        onContentMouseDown: handleContentMouseDown
    }, /*#__PURE__*/ React.createElement(CoreInput, {
        ...inputProps,
        role: "combobox",
        "aria-autocomplete": "both",
        "aria-controls": contentId,
        "aria-expanded": ariaExpanded,
        "aria-activedescendant": ariaActivedescendant ?? undefined,
        onKeyDown: handleKeyDown,
        onFocus: handleFocus,
        onBlur: handleBlur,
        className: classNames(inputWithOptionsClasses.inputComponent, classes.input)
    }));
};
FloatingInputWithOptions.displayName = 'FloatingInputWithOptions';
function getFilteredOptions(options, inputValue, isEditing, filterPredicate) {
    if (!inputValue || !isEditing) {
        return options;
    }
    return options.filter((option)=>!option.isSelectable || option.value !== undefined && filterPredicate(inputValue, option.value));
}

//# sourceMappingURL=floating-input-with-options.js.map